// useRecaptcha.ts
import { useEffect, useRef, useState, useCallback } from 'react';
import { RecaptchaVerifier , getAuth, type Auth } from 'firebase/auth';

type UseRecaptcha = {
  recaptchaContainerRef: (el: HTMLDivElement | null) => void; // callback ref
  recaptcha: RecaptchaVerifier | null;
  ready: boolean;
  widgetId: number | null;
  reset: () => Promise<void>;
};

export function useRecaptcha(): UseRecaptcha {
  const auth: Auth = getAuth();

  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);
  const widgetIdRef = useRef<number | null>(null);

  const [ready, setReady] = useState(false);

  // Callback ref so React will re-render when the element appears
  const containerRef = useCallback((el: HTMLDivElement | null) => {
    setContainerEl(el);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!containerEl) return;
      if (recaptchaRef.current) return; // already initialized

      try {
        // Correct modular order: (auth, container, parameters)
        const verifier = new RecaptchaVerifier(auth, containerEl, {
          size: 'invisible',
          callback: (token: string) => {
            // console.debug('reCAPTCHA callback token:', token);
          },
          'expired-callback': () => {
            // console.warn('reCAPTCHA expired');
            setReady(false);
          },
        });

        recaptchaRef.current = verifier;

        const widgetId = await verifier.render(); // must render or it won't be valid
        if (cancelled) return;

        widgetIdRef.current = widgetId;

        
console.log('Rendered reCAPTCHA widgetId:', widgetId);

        setReady(true);
      } catch (err) {
        console.error('reCAPTCHA render failed:', err);
        setReady(false);
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [auth, containerEl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try {
        recaptchaRef.current?.clear();
      } catch {}
      recaptchaRef.current = null;
      widgetIdRef.current = null;
      setReady(false);
    };
  }, []);

  const reset = async () => {
    try {
      if (recaptchaRef.current && widgetIdRef.current != null) {
        // Attempt a normal reset first
        // @ts-ignore grecaptcha exists after render
        window.grecaptcha?.reset(widgetIdRef.current);
        setReady(true);
        return;
      }
    } catch {
      // fall through
    }
    // Hard recreate if needed
    try {
      recaptchaRef.current?.clear();
    } catch {}
    recaptchaRef.current = null;
    widgetIdRef.current = null;
    setReady(false);
    // will auto re-init because containerEl is still set
  };

  return {
    recaptchaContainerRef: containerRef,
    recaptcha: recaptchaRef.current,
    ready,
    widgetId: widgetIdRef.current,
    reset,
  };
}
